import { readLiveArtifactMutationCapability } from '../../shell/pluginCapabilities';

const ARTIFACT_PUBLISH_TIMEOUT_MS = 120000;

function assertClientMethod(client, methodName, label) {
  if (!client || typeof client[methodName] !== 'function') {
    throw new Error(`${label} is not available. Reopen the embedded admin and try again.`);
  }
}

/**
 * Browser-owned artifact handoff:
 * 1. download inline artifact manifest from gateway;
 * 2. publish it to the local plugin bridge so PrestaShop persists it.
 *
 * The plugin/PHP side never fetches gateway URLs.
 */
export async function publishJobArtifactsToBridge({
  gatewayClient,
  bridgeClient,
  jobId,
  requestId = null,
  target = null,
}) {
  if (!jobId) {
    throw new Error('jobId is required');
  }

  assertClientMethod(gatewayClient, 'downloadJobArtifacts', 'Gateway artifact download');
  assertClientMethod(bridgeClient, 'sendMessageWithResponse', 'Plugin bridge');

  const artifacts = await gatewayClient.downloadJobArtifacts(jobId);
  const manifest = artifacts?.manifest;
  if (!manifest || typeof manifest !== 'object') {
    throw new Error('Gateway artifact download did not return a manifest');
  }

  const payload = {
    jobId: artifacts.jobId || jobId,
    requestId,
    manifest,
  };

  const isProductionPublish = target === 'production'
    || target === 'prod'
    || Object.prototype.hasOwnProperty.call(manifest, 'prod');
  if (isProductionPublish) {
    if (!artifacts.releaseId) {
      throw new Error('Gateway production artifact download did not return a release id');
    }
    payload.releaseId = artifacts.releaseId;
    if (!artifacts.settingsConfig || typeof artifacts.settingsConfig !== 'object') {
      throw new Error('Gateway production artifact download did not return its source settings');
    }
  }
  if (!Number.isSafeInteger(artifacts.activationFence) || artifacts.activationFence <= 0) {
    throw new Error('Gateway artifact download did not return an activation fence');
  }
  payload.activationFence = artifacts.activationFence;

  if (target) {
    payload.target = target;
  }

  // The shell capability is only a bootstrap snapshot. Re-read the plugin
  // contract at the actual mutation boundary so a stale iframe cannot publish
  // into an unknown or disconnected implementation. Marketplace 1.0.5+
  // installations are verified through app.bootstrap when the newer explicit
  // capability name is absent.
  const liveMutationCapability = await readLiveArtifactMutationCapability(bridgeClient);
  if (!liveMutationCapability) {
    throw new Error(
      'The embedded Search Plugin does not support artifact publication. '
      + 'Update the plugin, then reopen the admin.',
    );
  }

  const response = await bridgeClient.sendMessageWithResponse('artifacts.publish', payload, ARTIFACT_PUBLISH_TIMEOUT_MS);

  if (response?.result === 'error') {
    throw new Error(response.error || 'Plugin rejected artifact publish');
  }

  return {
    artifacts,
    response,
  };
}
